import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Group,
  MultiSelect,
  Select,
  Stack,
  Switch,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
  Alert,
  Container,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectUserDistributorId } from "@utils/redux-utils/selectors/userSelectors";
import { getAllCountryNames } from "@utils/CommonUtil";
import {
  createDistributor,
  editDistributor,
  getAllDistributors,
  getDistributor,
  getOrgsOfDistributor,
} from "@utils/api-communication/RestApiCli";

interface DistributorConfig {
  language: string | null;
  forcedPassChangeFirstLogin: boolean;
}

interface AccountManagementConfig {
  distAbbreviation: string;
}

interface DistributorModel {
  name: string;
  country: string[];
  billingAddress: string;
  billingEmail?: string;
  accountManagementConfig?: AccountManagementConfig;
  emails: string[];
  phone?: string;
  distId: string;
  orgIds?: string[];
  config?: DistributorConfig;
}

const languages = [
  { value: "none", label: "None" },
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
];

function useResolvedDistId(): string | null {
  const [searchParams] = useSearchParams();
  const fromUrl = searchParams.get("distId");
  const userDistId = useSelector(selectUserDistributorId);
  return useMemo(() => fromUrl || userDistId || null, [fromUrl, userDistId]);
}

export default function Distributor() {
  const navigate = useNavigate();
  const userDistId = useSelector(selectUserDistributorId);
  const resolvedDistId = useResolvedDistId();
  const [searchParams] = useSearchParams();
  const isCreate = searchParams.get("create") === "true" || searchParams.has("create");
  const [loading, setLoading] = useState<boolean>(false);
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const [orgOptions, setOrgOptions] = useState<{ value: string; label: string }[]>([]);
  const [orgOptionsLoading, setOrgOptionsLoading] = useState<boolean>(false);
  const [knownDistributorIds, setKnownDistributorIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(isCreate);

  const form = useForm<DistributorModel>({
    mode: "controlled",
    initialValues: {
      name: "",
      country: [],
      billingAddress: "",
      billingEmail: "",
      accountManagementConfig: {
        distAbbreviation: "",
      },
      emails: [],
      phone: "",
      distId: resolvedDistId ?? "",
      orgIds: [],
      config: {
        language: "none",
        forcedPassChangeFirstLogin: false,
      },
    },
    validate: {
      name: (v) => (!v?.trim() ? "Name is required" : null),
      billingEmail: (v) => (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "Invalid email" : null),
      emails: (v) =>
        v?.some((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) ? "All emails must be valid" : null,
      distId: (v) => {
        const normalized = v?.trim();
        if (!normalized) return "distId is required";
        if (isCreate && knownDistributorIds.includes(normalized)) {
          return "This distributor ID is already in use";
        }
        return null;
      },
      accountManagementConfig: {
        distAbbreviation: (v) => (v && v.length > 8 ? "Max 8 chars" : null),
      },
    },
  });

  useEffect(() => {
    getAllDistributors(
      (response) => {
        const ids = (response?.data ?? [])
          .map((dist: DistributorModel) => dist?.distId)
          .filter((distId: string | undefined): distId is string => !!distId);
        setKnownDistributorIds(ids);
      },
      (err) => {
        console.error("Failed to load distributor ids", err);
      },
    );
  }, []);

  useEffect(() => {
    getAllCountryNames()
      .then((countries) => setCountryOptions(countries))
      .catch((err) => {
        console.error("Failed to load country list", err);
        setCountryOptions([]);
      });
  }, []);

  // Fetch distributor data when distId resolves (skip in create mode)
  useEffect(() => {
    if (!resolvedDistId || isCreate) return;
    setLoading(true);
    setError(null);
    getDistributor(
      resolvedDistId,
      (response) => {
        const d = response?.data || {};
        form.setValues({
          name: d.name ?? "",
          country: d.country ?? [],
          billingAddress: d.billingAddress ?? "",
          billingEmail: d.billingEmail ?? "",
          accountManagementConfig: {
            distAbbreviation: d.accountManagementConfig?.distAbbreviation ?? "",
          },
          emails: d.emails ?? [],
          phone: d.phone ?? "",
          distId: d.distId ?? resolvedDistId ?? "",
          orgIds: d.orgIds ?? [],
          config: {
            language: d.config?.language ?? "none",
            forcedPassChangeFirstLogin: d.config?.forcedPassChangeFirstLogin ?? false,
          },
        });
        setLoading(false);
      },
      (err) => {
        setError("Failed to load distributor details");
        setLoading(false);
        // eslint-disable-next-line no-console
        console.error(err);
      },
    );
  }, [resolvedDistId, isCreate]);

  // Keep distId in sync if URL/store changes
  useEffect(() => {
    if (resolvedDistId && form.values.distId !== resolvedDistId) {
      form.setFieldValue("distId", resolvedDistId);
    }
  }, [resolvedDistId]);

  useEffect(() => {
    const distId = (isCreate ? form.values.distId : resolvedDistId || userDistId)?.trim();

    if (!distId) {
      setOrgOptions([]);
      if (form.values.orgIds.length > 0) {
        form.setFieldValue("orgIds", []);
      }
      return;
    }

    setOrgOptionsLoading(true);
    getOrgsOfDistributor(
      { distId },
      (response) => {
        const options = (response?.data ?? []).map((org: { orgId: string; name?: string }) => ({
          value: org.orgId,
          label: org.name ? `${org.orgId} - ${org.name}` : org.orgId,
        }));
        setOrgOptions(options);
        form.setFieldValue(
          "orgIds",
          form.values.orgIds.filter((orgId) => options.some((option) => option.value === orgId)),
        );
        setOrgOptionsLoading(false);
      },
      (err) => {
        console.error("Failed to load organizations for distributor", err);
        setOrgOptions([]);
        form.setFieldValue("orgIds", []);
        setOrgOptionsLoading(false);
      },
    );
  }, [form.values.distId, isCreate, resolvedDistId, userDistId]);

  const handleSubmit = (values: DistributorModel) => {
    const trimmedDistId = values.distId.trim();

    if (isCreate && knownDistributorIds.includes(trimmedDistId)) {
      form.setFieldError("distId", "This distributor ID is already in use");
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      ...values,
      distId: trimmedDistId,
      config: {
        ...values.config,
        language: values.config?.language === "none" ? null : (values.config?.language ?? null),
      },
    };

    const onSuccess = () => {
      setKnownDistributorIds((current) =>
        current.includes(trimmedDistId) ? current : [...current, trimmedDistId],
      );
      setEditMode(false);
      setLoading(false);
      if (isCreate) {
        navigate("/dist");
      }
    };

    const onError = (err: any) => {
      const message = err?.response?.data?.message || "Failed to save distributor";
      if (/unique|duplicate|already exists|already in use/i.test(message)) {
        form.setFieldError("distId", "This distributor ID is already in use");
        setError("Distributor ID must be unique.");
      } else {
        setError(message);
      }
      setLoading(false);
    };

    if (isCreate) {
      createDistributor(payload, onSuccess, onError);
      return;
    }

    editDistributor(payload, onSuccess, onError);
  };

  const disabled = !editMode;
  const isExisting = !!resolvedDistId && !isCreate;

  return (
    <Container>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>{isCreate ? "Create Distributor" : "Distributor Editor"}</Title>
          <Group>
            {!editMode && !isCreate && (
              <Button onClick={() => setEditMode(true)} disabled={loading || !!error}>
                Edit
              </Button>
            )}
          </Group>
        </Group>

        {!resolvedDistId && !isCreate && (
          <Alert color="yellow" title="No distId">
            distId not found in URL. Using user distributorId if available. You can also edit it
            below.
          </Alert>
        )}

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}

        {loading && !isCreate && <Text c="dimmed">Loading distributor...</Text>}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="lg">
            <Box>
              <Title order={5}>General</Title>
              <Divider my="sm" />
              <Stack gap="sm">
                <TextInput
                  withAsterisk
                  label="Name"
                  placeholder="Distributor name"
                  key={form.key("name")}
                  disabled={disabled}
                  {...form.getInputProps("name")}
                />

                <MultiSelect
                  label="Countries"
                  description="Select one or more countries"
                  placeholder="Select countries"
                  data={countryOptions}
                  searchable
                  clearable
                  nothingFoundMessage="No countries found"
                  key={form.key("country")}
                  disabled={disabled}
                  {...form.getInputProps("country")}
                />

                <Textarea
                  label="Billing address"
                  minRows={2}
                  autosize
                  placeholder="Street, City, ZIP, Country"
                  key={form.key("billingAddress")}
                  disabled={disabled}
                  {...form.getInputProps("billingAddress")}
                />

                <TextInput
                  label="Billing email"
                  placeholder="billing@example.com"
                  key={form.key("billingEmail")}
                  disabled={disabled}
                  {...form.getInputProps("billingEmail")}
                />

                <TextInput
                  label="Phone"
                  placeholder="+1 555 123 4567"
                  key={form.key("phone")}
                  disabled={disabled}
                  {...form.getInputProps("phone")}
                />

                <TextInput
                  label="Distributor ID"
                  description={
                    isCreate
                      ? "Enter a unique distributor ID"
                      : "Pre-filled from URL (?distId=...) or your account"
                  }
                  readOnly={isExisting}
                  disabled={isExisting ? true : disabled}
                  key={form.key("distId")}
                  {...form.getInputProps("distId")}
                />

                <MultiSelect
                  label="Organization IDs"
                  placeholder={
                    (isCreate ? form.values.distId : resolvedDistId || userDistId)?.trim()
                      ? "Select organization IDs"
                      : "Enter or resolve a distributor ID first"
                  }
                  description="Organizations linked to the distributor currently being edited"
                  data={orgOptions}
                  searchable
                  clearable
                  nothingFoundMessage={
                    (isCreate ? form.values.distId : resolvedDistId || userDistId)?.trim()
                      ? "No organizations found for this distributor ID"
                      : "No distributor ID available"
                  }
                  key={form.key("orgIds")}
                  disabled={
                    disabled ||
                    !(isCreate ? form.values.distId : resolvedDistId || userDistId)?.trim()
                  }
                  rightSection={orgOptionsLoading ? <Text size="xs">...</Text> : undefined}
                  {...form.getInputProps("orgIds")}
                />

                <TagsInput
                  label="Contact emails"
                  placeholder="email@example.com"
                  splitChars={[",", ";", " "]}
                  key={form.key("emails")}
                  disabled={disabled}
                  {...form.getInputProps("emails")}
                />
              </Stack>
            </Box>

            <Box>
              <Title order={5}>Account management</Title>
              <Divider my="sm" />
              <Group align="end" gap="md" wrap="wrap">
                <TextInput
                  label="Distributor abbreviation"
                  placeholder="e.g., ACME"
                  key={form.key("accountManagementConfig.distAbbreviation")}
                  disabled={disabled}
                  {...form.getInputProps("accountManagementConfig.distAbbreviation")}
                />
              </Group>
            </Box>

            <Box>
              <Title order={5}>Configuration</Title>
              <Divider my="sm" />
              <Group align="center" gap="md" wrap="wrap">
                <Select
                  label="Distributor Language"
                  data={languages}
                  allowDeselect={false}
                  key={form.key("config.language")}
                  disabled={disabled}
                  {...form.getInputProps("config.language")}
                />
                <Switch
                  label="Force password change on first login"
                  key={form.key("config.forcedPassChangeFirstLogin")}
                  disabled={disabled}
                  {...form.getInputProps("config.forcedPassChangeFirstLogin", { type: "checkbox" })}
                />
              </Group>
            </Box>

            {/* Actions */}
            <Group justify="space-between">
              {editMode ? (
                <Group>
                  <Button
                    variant="default"
                    onClick={() => {
                      if (isCreate) {
                        navigate("/dist");
                        return;
                      }
                      setEditMode(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">Save</Button>
                </Group>
              ) : null}
            </Group>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}
